import React, { useState } from 'react';
import {
  Button,
  Modal,
  Spin,
  Typography,
  message,
  Dropdown,
  Tooltip,
} from 'antd';
import { DownloadOutlined, DownOutlined } from '@ant-design/icons';
import { useApiUrl } from '@refinedev/core';
import { apiClient } from '../../../utils/axios';
import { ApiConfig } from '../types';
import { getPatientSearchParamValues } from '@src/utils/patientSearchFilters';

const { Text } = Typography;

export interface ExportFormat {
  key: string;
  label: string;
  format: 'csv' | 'excel' | 'pdf' | 'json';
  icon?: React.ReactNode;
}

export interface ExportComponentProps {
  api: ApiConfig;
  searchValue?: string;
  searchFields?: string[];
  activeFilters?: any[];
  exportFormats?: ExportFormat[];
  defaultFormat?: 'csv' | 'excel' | 'pdf' | 'json';
  showExport?: boolean;
  requireFiltersForExport?: boolean; // New prop to require filters before export
  buttonText?: string;
  buttonProps?: any;
}

export const ExportComponent: React.FC<ExportComponentProps> = ({
  api,
  searchValue = '',
  searchFields = [],
  activeFilters = [],
  exportFormats = [
    { key: 'csv', label: 'Export as CSV', format: 'csv' as const },
    // { key: 'excel', label: 'Export as Excel', format: 'excel' as const },
  ],
  defaultFormat = 'csv',
  showExport = true,
  requireFiltersForExport = false,
  buttonText = 'Export',
  buttonProps = {},
}) => {
  const [exportLoading, setExportLoading] = useState(false);
  const apiUrl = useApiUrl();

  // Check if filters are applied
  const hasActiveFilters = activeFilters.length > 0;

  const handleExport = async (format: 'csv' | 'excel' | 'pdf' | 'json') => {
    try {
      setExportLoading(true);

      // Build query parameters
      const queryParams: Record<string, any> = { format };

      // Add search filters
      if (searchValue && searchFields.length > 0) {
        if (api.resource === 'patients') {
          const searchParams = getPatientSearchParamValues(
            searchValue,
            searchFields
          );
          Object.entries(searchParams).forEach(([field, val]) => {
            queryParams[`search[${field}]`] = val;
          });
        } else {
          searchFields.forEach(field => {
            queryParams[`search[${field}]`] = searchValue;
          });
        }
      }

      // Add advanced filters
      if (activeFilters.length > 0) {
        activeFilters.forEach(filter => {
          if (filter.operator === 'contains') {
            queryParams[`filter[${filter.field}_like]`] = filter.value;
          } else if (filter.operator === 'eq') {
            queryParams[`filter[${filter.field}]`] = filter.value;
          } else if (filter.operator === 'gte') {
            queryParams[`filter[${filter.field}_gte]`] = filter.value;
          } else if (filter.operator === 'lte') {
            queryParams[`filter[${filter.field}_lte]`] = filter.value;
          } else if (filter.operator === 'in') {
            queryParams[`filter[${filter.field}_in]`] = Array.isArray(
              filter.value
            )
              ? filter.value.join(',')
              : filter.value;
          } else {
            queryParams[`filter[${filter.field}]`] = filter.value;
          }
        });
      }

      // Call export using apiClient (same as data provider)
      const response = await apiClient.get(`/${api.resource}/export`, {
        params: queryParams,
        responseType: 'blob',
      });

      if (response.data) {
        // Trigger file download
        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${api.resource}_export_${
          new Date().toISOString().split('T')[0]
        }.${format}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        message.success('Export completed successfully!');
      }
    } catch (error: any) {
      message.error(error?.message || 'Export failed. Please try again.');
    } finally {
      setExportLoading(false);
    }
  };

  // Create menu items for dropdown
  const menuItems = exportFormats.map(format => ({
    key: format.key,
    label: format.label,
    icon: format.icon,
    onClick: () => handleExport(format.format),
  }));

  if (!showExport) {
    return null;
  }

  // Determine if button should be disabled
  const isDisabled =
    exportLoading || (requireFiltersForExport && !hasActiveFilters);

  // Tooltip message when disabled due to filters
  const tooltipMessage =
    requireFiltersForExport && !hasActiveFilters
      ? 'Please apply at least one filter before exporting'
      : '';

  return (
    <>
      <Tooltip title={tooltipMessage}>
        <Dropdown
          menu={{ items: menuItems }}
          disabled={isDisabled}
          trigger={['click']}
        >
          <Button
            icon={<DownloadOutlined />}
            loading={exportLoading}
            disabled={isDisabled}
            {...buttonProps}
          >
            {buttonText} <DownOutlined />
          </Button>
        </Dropdown>
      </Tooltip>

      {/* Export Loading Modal */}
      <Modal
        title="Generating Export"
        open={exportLoading}
        footer={null}
        closable={false}
        maskClosable={false}
        centered
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text>Please wait while we generate your export file...</Text>
          </div>
          <div style={{ marginTop: 8 }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              This may take a few moments for large datasets
            </Text>
          </div>
        </div>
      </Modal>
    </>
  );
};
